import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit2, Trash2, Search, X, Save, Ban, Copy, FileText, AlertCircle, Printer, HelpCircle } from 'lucide-react';
import apiClient from '../services/api';

const NAVY       = '#0d1c6b';
const NAVY_LIGHT = '#1a3a8f';

const JournalEntry = () => {
  const getSessionData = () => {
    try {
      const s = localStorage.getItem('ltms_session');
      if (!s) return null;
      const d = JSON.parse(s);
      return d?.session || d;
    } catch { return null; }
  };

  const session   = getSessionData();
  const companyId = session?.companyid;
  const yearId    = session?.yearid;
  const printRef  = useRef();

  const [vouchers,      setVouchers]      = useState([]);
  const [accounts,      setAccounts]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [showModal,     setShowModal]     = useState(false);
  const [editMode,      setEditMode]      = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [errors,        setErrors]        = useState({});
  const [showHelp,      setShowHelp]      = useState(false);

  const [formData, setFormData] = useState({
    acjournalid: null, companyid: companyId || null, fyearid: yearId || null,
    voucherno: '', transdate: new Date().toISOString().split('T')[0],
    vouchertype: 'JV', narration: '', details: [],
  });

  const [currentDetail, setCurrentDetail] = useState({ acheadid: '', debitAmount: '', creditAmount: '', narration: '' });

  useEffect(() => {
    if (!companyId || !yearId) { setLoading(false); return; }
    fetchInitialData();
  }, [companyId, yearId]);

  const fetchInitialData = async () => {
    setLoading(true);
    try { await Promise.all([fetchAccounts(), fetchVouchers()]); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchAccounts = async () => {
    try {
      const res = await apiClient.get('/Acheads.php', { params: { companyid: companyId } });
      if (res.data.success) setAccounts(res.data.data || []);
    } catch (err) { if (err.response?.status === 401) alert('Session expired. Please login again.'); }
  };

  const fetchVouchers = async () => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { companyid: companyId, fyearid: yearId, vouchertype: 'JV' } });
      if (res.data.success) {
        // Only show entries created through this form (JV prefix)
        const jvOnly = (res.data.data || []).filter(v => v.voucherno?.startsWith('JV'));
        setVouchers(jvOnly);
      }
    } catch (err) { if (err.response?.status === 401) alert('Session expired. Please login again.'); }
  };

  const getNextVoucherNumber = async () => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { nextvoucher: true, companyid: companyId, fyearid: yearId, vouchertype: 'JV' } });
      if (res.data.success) setFormData(p => ({ ...p, voucherno: res.data.data.voucherno }));
    } catch (e) { console.error(e); }
  };

  const mapDetails = (details) => details.map(d => ({
    acheadid: d.acheadid, acheadname: d.acheadname, acheadcode: d.acheadcode || '',
    debitAmount: d.amount > 0 ? d.amount : 0, creditAmount: d.amount < 0 ? Math.abs(d.amount) : 0, narration: d.remarks || '',
  }));

  const handleNew = async () => { resetForm(); await getNextVoucherNumber(); setEditMode(false); setShowModal(true); };

  const handleEdit = async (voucher) => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { id: voucher.acjournalid } });
      if (res.data.success) {
        const { master, details } = res.data.data;
        setFormData({ acjournalid: master.acjournalid, companyid: master.companyid, fyearid: master.fyearid, voucherno: master.voucherno, transdate: master.transdate, vouchertype: master.vouchertype, narration: master.remarks || '', details: mapDetails(details) });
        setEditMode(true); setShowModal(true);
      }
    } catch { alert('Error loading voucher details'); }
  };

  const handleDuplicate = async (voucher) => {
    try {
      const res = await apiClient.get('/Vouchers.php', { params: { id: voucher.acjournalid } });
      if (res.data.success) {
        const { master, details } = res.data.data;
        await getNextVoucherNumber();
        setFormData(p => ({ ...p, acjournalid: null, transdate: new Date().toISOString().split('T')[0], narration: master.remarks || '', details: mapDetails(details) }));
        setEditMode(false); setShowModal(true);
      }
    } catch { alert('Error duplicating voucher'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this journal entry?\n\nThis action cannot be undone.')) return;
    try {
      const res = await apiClient.delete('/Vouchers.php', { data: { acjournalid: id } });
      if (res.data.success) { alert('Journal entry deleted successfully!'); fetchVouchers(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error deleting voucher. Please try again.');
    }
  };

  const handlePrint = () => {
    if (formData.details.length === 0) { alert('Please add transaction details before printing'); return; }
    if (!isBalanced()) { alert('Journal entry must be balanced before printing'); return; }
    const printWindow = window.open('', '', 'height=600,width=800');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Journal Entry - ${formData.voucherno}</title><style>*{margin:0;padding:0;box-sizing:border-box;}body{font-family:Arial,sans-serif;padding:20mm;background:white;}.print-header{text-align:center;margin-bottom:30px;border-bottom:3px solid #000;padding-bottom:20px;}.company-name{font-size:24px;font-weight:bold;margin-bottom:5px;}.voucher-title{font-size:18px;font-weight:bold;margin-top:10px;}.voucher-info{display:flex;justify-content:space-between;margin:20px 0;padding:15px;background:#f5f5f5;border:1px solid #ddd;}.info-item{display:flex;flex-direction:column;}.info-label{font-size:11px;color:#666;text-transform:uppercase;margin-bottom:4px;}.info-value{font-size:14px;font-weight:bold;font-family:'Courier New',monospace;}.details-table{width:100%;border-collapse:collapse;margin:20px 0;}.details-table th{background:#000;color:white;padding:12px 8px;text-align:left;font-size:12px;text-transform:uppercase;font-weight:bold;}.details-table td{padding:10px 8px;border-bottom:1px solid #ddd;font-size:13px;}.debit{color:#dc2626;}.credit{color:#059669;}.amount{text-align:right;font-family:'Courier New',monospace;font-weight:bold;}.totals-section{margin:20px 0;border-top:3px double #000;padding-top:15px;}.total-row{display:flex;justify-content:space-between;padding:8px 0;font-size:14px;}.total-value{font-family:'Courier New',monospace;font-weight:bold;}.signatures{display:flex;justify-content:space-between;margin-top:60px;padding-top:20px;border-top:1px solid #ddd;}.signature-box{text-align:center;width:200px;}.signature-line{border-top:2px solid #000;margin-top:60px;margin-bottom:8px;}.signature-label{font-size:12px;color:#666;}.footer{margin-top:40px;text-align:center;font-size:11px;color:#999;border-top:1px solid #ddd;padding-top:15px;}</style></head><body><div class="print-header"><div class="company-name">${session?.companyname||'Company Name'}</div><div class="voucher-title">JOURNAL VOUCHER</div></div><div class="voucher-info"><div class="info-item"><span class="info-label">Voucher No</span><span class="info-value">${formData.voucherno}</span></div><div class="info-item"><span class="info-label">Date</span><span class="info-value">${new Date(formData.transdate).toLocaleDateString('en-GB')}</span></div><div class="info-item"><span class="info-label">Type</span><span class="info-value">JV</span></div></div><table class="details-table"><thead><tr><th>#</th><th>Account Name</th><th style="text-align:right">Debit (Dr)</th><th style="text-align:right">Credit (Cr)</th><th>Narration</th></tr></thead><tbody>${formData.details.map((d,i)=>`<tr><td style="text-align:center">${i+1}</td><td><strong>${d.acheadname}</strong>${d.acheadcode?`<br><small>${d.acheadcode}</small>`:''}</td><td class="amount debit">${d.debitAmount>0?`₹ ${parseFloat(d.debitAmount).toLocaleString('en-IN',{minimumFractionDigits:2})}`:'-'}</td><td class="amount credit">${d.creditAmount>0?`₹ ${parseFloat(d.creditAmount).toLocaleString('en-IN',{minimumFractionDigits:2})}`:'-'}</td><td style="font-style:italic;color:#666">${d.narration||'-'}</td></tr>`).join('')}</tbody></table><div class="totals-section"><div class="total-row"><span>Total Debit:</span><span class="total-value debit">₹ ${calculateDebitTotal().toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div><div class="total-row"><span>Total Credit:</span><span class="total-value credit">₹ ${calculateCreditTotal().toLocaleString('en-IN',{minimumFractionDigits:2})}</span></div></div>${formData.narration?`<div style="margin:20px 0;padding:15px;background:#f9f9f9;border-left:4px solid #667eea"><div style="font-size:11px;color:#666;text-transform:uppercase;margin-bottom:8px;font-weight:bold">Narration</div><div style="font-size:13px;line-height:1.6">${formData.narration}</div></div>`:''}<div class="signatures"><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Prepared By</div></div><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Verified By</div></div><div class="signature-box"><div class="signature-line"></div><div class="signature-label">Approved By</div></div></div><div class="footer"><p>This is a computer-generated document.</p><p>Printed on: ${new Date().toLocaleString('en-GB')}</p></div></body></html>`);
    printWindow.document.close(); printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const addDetailLine = () => {
    if (!currentDetail.acheadid) { alert('Please select an account'); return; }
    const hasDebit  = currentDetail.debitAmount  && parseFloat(currentDetail.debitAmount)  > 0;
    const hasCredit = currentDetail.creditAmount && parseFloat(currentDetail.creditAmount) > 0;
    if (!hasDebit && !hasCredit) { alert('Please enter either debit or credit amount'); return; }
    if (hasDebit && hasCredit)   { alert('Cannot enter both debit and credit for the same line'); return; }
    const account = accounts.find(a => a.acheadid === parseInt(currentDetail.acheadid));
    if (!account) { alert('Invalid account selected'); return; }
    setFormData(p => ({ ...p, details: [...p.details, { acheadid: parseInt(currentDetail.acheadid), acheadname: account.acheadname, acheadcode: account.acheadcode || '', debitAmount: hasDebit ? parseFloat(currentDetail.debitAmount) : 0, creditAmount: hasCredit ? parseFloat(currentDetail.creditAmount) : 0, narration: currentDetail.narration }] }));
    setCurrentDetail({ acheadid: '', debitAmount: '', creditAmount: '', narration: '' });
  };

  const removeDetailLine    = (index) => setFormData(p => ({ ...p, details: p.details.filter((_, i) => i !== index) }));
  const calculateDebitTotal  = () => formData.details.reduce((s, d) => s + parseFloat(d.debitAmount  || 0), 0);
  const calculateCreditTotal = () => formData.details.reduce((s, d) => s + parseFloat(d.creditAmount || 0), 0);
  const getDifference        = () => calculateDebitTotal() - calculateCreditTotal();
  const isBalanced           = () => Math.abs(getDifference()) < 0.01;

  const fmtINR = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const validateForm = () => {
    const errs = {};
    if (!formData.voucherno.trim())    errs.voucherno = 'Voucher number is required';
    if (!formData.transdate)           errs.transdate = 'Transaction date is required';
    if (formData.details.length === 0) errs.details   = 'At least one detail line is required';
    if (formData.details.length === 1) errs.details   = 'Journal entry requires at least 2 lines (debit and credit)';
    if (!isBalanced())                 errs.balance   = 'Journal entry must be balanced (Total Debit = Total Credit)';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitLoading(true);
    try {
      const payload = { companyid: companyId, fyearid: yearId, voucherno: formData.voucherno, transdate: formData.transdate, vouchertype: formData.vouchertype, remarks: formData.narration, details: formData.details.map(d => ({ acheadid: d.acheadid, amount: d.debitAmount > 0 ? d.debitAmount : -d.creditAmount, remarks: d.narration || '' })) };
      if (editMode) payload.acjournalid = formData.acjournalid;
      const res = await apiClient[editMode ? 'put' : 'post']('/Vouchers.php', payload);
      if (res.data.success) { alert(editMode ? 'Journal entry updated successfully!' : 'Journal entry created successfully!'); setShowModal(false); resetForm(); fetchVouchers(); }
      else alert('Error: ' + res.data.message);
    } catch (err) {
      if (err.response?.status === 401) alert('Session expired. Please login again.');
      else alert('Error saving journal entry. Please try again.');
    } finally { setSubmitLoading(false); }
  };

  const resetForm = () => {
    setFormData({ acjournalid: null, companyid: companyId || null, fyearid: yearId || null, voucherno: '', transdate: new Date().toISOString().split('T')[0], vouchertype: 'JV', narration: '', details: [] });
    setCurrentDetail({ acheadid: '', debitAmount: '', creditAmount: '', narration: '' });
    setErrors({});
  };

  const closeModal = () => {
    if (formData.details.length > 0) { if (!window.confirm('You have unsaved changes. Are you sure you want to close?')) return; }
    setShowModal(false); resetForm();
  };

  const labelCls  = 'block mb-1.5 text-xs font-bold text-gray-600 uppercase tracking-wide';
  const inputCls  = (err) => `w-full px-3 py-2.5 border rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] ${err ? 'border-red-400 bg-red-50' : 'border-gray-300'}`;
  const errMsg    = (msg) => msg ? <p className="text-xs text-red-600 font-semibold mt-1">⚠ {msg}</p> : null;

  const filteredVouchers = vouchers.filter(v =>
    v.voucherno?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    v.remarks?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!companyId || !yearId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center p-10 bg-white rounded-xl shadow-sm border border-gray-200">
          <FileText size={40} className="mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-bold text-gray-700 mb-2">No Session Context</h3>
          <p className="text-gray-500 text-sm">Please select a company and financial year from the dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 min-h-screen">

      {/* ── HEADER ── */}
      <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
        <div>
          <h1 className="text-xl font-bold m-0" style={{ color: '#fff' }}>📒 Journal Voucher Entry</h1>
          <p className="text-[13px] mt-1" style={{ color: 'rgba(255,255,255,0.8)' }}>Record general journal entries with proper double-entry accounting</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowHelp(true)}
            className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg border-0 cursor-pointer transition-all duration-150 hover:bg-white/30"
            style={{ color: '#fff' }} title="How this page works">
            <HelpCircle size={18} />
          </button>
          <button onClick={handleNew}
            className="flex items-center gap-2 px-4 py-2.5 bg-white rounded-lg border-0 text-sm font-bold cursor-pointer transition-all duration-150 hover:bg-blue-50"
            style={{ color: NAVY }}>
            <Plus size={17} /> New Journal Entry
          </button>
        </div>
      </div>

      {/* ── TOOLBAR ── */}
      <div className="flex items-center gap-3 flex-wrap px-5 py-3 bg-white border-b border-gray-200 flex-shrink-0">
        <div className="flex-1 min-w-[220px] flex items-center gap-2 bg-gray-50 border-2 border-gray-200 rounded-lg px-3
          focus-within:border-[#0d1c6b] focus-within:bg-white focus-within:shadow-[0_0_0_4px_rgba(13,28,107,0.1)] transition-all duration-200">
          <Search size={16} className="text-gray-400 flex-shrink-0" />
          <input type="text" placeholder="Search by voucher number or narration..."
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 border-0 outline-none py-2.5 text-sm text-gray-800 bg-transparent font-medium placeholder:text-gray-400 placeholder:font-normal" />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')}
              className="p-1 rounded cursor-pointer bg-transparent border-0 text-gray-400 hover:text-red-500 hover:bg-gray-100">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="text-sm font-semibold text-gray-500 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-lg whitespace-nowrap">
          <strong>{filteredVouchers.length}</strong> {filteredVouchers.length === 1 ? 'entry' : 'entries'}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="flex-1 p-5">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400 text-lg font-semibold">Loading journal entries...</div>
        ) : filteredVouchers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-gray-200 gap-3">
            <FileText size={44} className="text-gray-300" />
            <h3 className="text-lg font-bold text-gray-600">No Journal Entries Found</h3>
            <p className="text-gray-400 text-sm">{searchTerm ? 'No entries match your search' : 'Click "New Journal Entry" to create your first entry'}</p>
            {searchTerm && <button onClick={() => setSearchTerm('')} className="text-sm text-blue-600 underline cursor-pointer bg-transparent border-0">Clear Search</button>}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-xl border border-gray-200 shadow-sm">
            <table className="w-full border-collapse">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                  {['Voucher No', 'Date', 'Debit Amount', 'Credit Amount', 'Narration', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3.5 text-left text-[11px] font-bold uppercase tracking-wider whitespace-nowrap sticky top-0"
                      style={{ color: '#fff', background: 'inherit' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredVouchers.map(voucher => (
                  <tr key={voucher.acjournalid}
                    className="border-b border-gray-100 transition-all duration-150 hover:bg-gray-50 hover:translate-x-0.5 hover:shadow-[-2px_0_0_#0d1c6b]">
                    <td className="px-4 py-3.5">
                      <span className="font-mono text-[12px] font-bold text-violet-700 bg-violet-50 px-2 py-0.5 rounded">{voucher.voucherno}</span>
                    </td>
                    <td className="px-4 py-3.5 text-sm text-gray-600">{new Date(voucher.transdate).toLocaleDateString('en-GB')}</td>
                    <td className="px-4 py-3.5 text-right font-mono font-semibold text-red-600">₹ {fmtINR(voucher.total_amount)}</td>
                    <td className="px-4 py-3.5 text-right font-mono font-semibold text-emerald-600">₹ {fmtINR(voucher.total_amount)}</td>
                    <td className="px-4 py-3.5 text-sm text-gray-500 max-w-[220px] truncate">{voucher.remarks || '-'}</td>
                    <td className="px-4 py-3.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => handleEdit(voucher)} title="Edit"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-violet-100 border-violet-400 text-violet-700 transition-all duration-150 hover:bg-violet-700 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Edit2 size={13} />
                        </button>
                        <button onClick={() => handleDuplicate(voucher)} title="Duplicate"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-blue-100 border-blue-400 text-blue-700 transition-all duration-150 hover:bg-blue-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Copy size={13} />
                        </button>
                        <button onClick={() => handleDelete(voucher.acjournalid)} title="Delete"
                          className="w-8 h-8 flex items-center justify-center rounded-lg border-2 cursor-pointer bg-red-100 border-red-400 text-red-600 transition-all duration-150 hover:bg-red-600 hover:text-white hover:scale-110 hover:-translate-y-0.5">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── MODAL ── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4"
          style={{ animation: 'fadeIn 0.2s ease' }}>
          <div className="bg-white rounded-2xl w-full max-w-[1000px] shadow-2xl border border-gray-200 overflow-hidden max-h-[92vh] flex flex-col"
            style={{ animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)' }}
            onClick={e => e.stopPropagation()}>

            <div className="flex items-center justify-between px-7 py-5 flex-shrink-0"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <div>
                <h3 className="text-[18px] font-bold uppercase tracking-wide m-0" style={{ color: '#fff' }}>
                  {editMode ? '✏️ Edit' : '➕ New'} Journal Entry
                </h3>
                <p className="text-[12px] m-0" style={{ color: 'rgba(255,255,255,0.75)' }}>Double-entry bookkeeping system</p>
              </div>
              <button onClick={closeModal}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white transition-all duration-200 hover:bg-white/30 hover:rotate-90"
                style={{ color: '#fff' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-7 space-y-5">

              {/* ── Voucher Details ── */}
              <div className="px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest mb-4">📋 Voucher Details</div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>
                      Voucher No
                      <span className="ml-2 inline-block px-1.5 py-0.5 bg-emerald-500 text-white text-[9px] font-bold rounded uppercase">Auto</span>
                    </label>
                    <div className="relative flex items-center">
                      <input type="text" value={formData.voucherno} readOnly placeholder="Auto-generating..."
                        className="w-full pr-8 px-3 py-2.5 border-2 border-gray-200 rounded-lg text-sm font-mono font-semibold bg-gray-100 text-gray-700 cursor-not-allowed outline-none" />
                      <span className="absolute right-2.5 text-lg opacity-50 pointer-events-none">🔒</span>
                    </div>
                    {errMsg(errors.voucherno)}
                  </div>
                  <div>
                    <label className={labelCls}>Date <span className="text-red-500">*</span></label>
                    <input type="date" value={formData.transdate}
                      onChange={e => setFormData({ ...formData, transdate: e.target.value })}
                      className={inputCls(errors.transdate)} />
                    {errMsg(errors.transdate)}
                  </div>
                  <div>
                    <label className={labelCls}>Type</label>
                    <input type="text" value="Journal Voucher (JV)" disabled
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold bg-gray-100 text-gray-600 cursor-not-allowed outline-none" />
                  </div>
                </div>
              </div>

              {/* ── Transaction Details ── */}
              <div className="px-4 py-4 bg-gray-50 border border-gray-200 rounded-xl">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-widest">💳 Transaction Details <span className="text-red-500">*</span></div>
                  {errors.details && (
                    <div className="flex items-center gap-1.5 text-xs text-red-600 font-semibold">
                      <AlertCircle size={13} /> {errors.details}
                    </div>
                  )}
                </div>

                {/* Add detail line */}
                <div className="p-4 bg-white border border-gray-200 rounded-xl mb-4">
                  <div className="grid grid-cols-[1fr_1fr_1fr_1fr_1fr_auto] gap-3 items-end">
                    <div className="col-span-2">
                      <label className={labelCls}>Account Ledger <span className="text-red-500">*</span></label>
                      <select value={currentDetail.acheadid}
                        onChange={e => setCurrentDetail({ ...currentDetail, acheadid: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none cursor-pointer focus:border-[#0d1c6b]">
                        <option value="">-- Select Account Ledger --</option>
                        {accounts.map(a => (
                          <option key={a.acheadid} value={a.acheadid}>
                            {a.acheadcode ? `${a.acheadcode} - ` : ''}{a.acheadname}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Debit (Dr)</label>
                      <input type="number" value={currentDetail.debitAmount} placeholder="0.00" step="0.01" min="0"
                        onChange={e => setCurrentDetail({ ...currentDetail, debitAmount: e.target.value, creditAmount: '' })}
                        className="w-full px-3 py-2.5 border border-red-200 bg-red-50 rounded-lg text-sm text-right font-mono outline-none focus:border-red-400" />
                    </div>
                    <div>
                      <label className={labelCls}>Credit (Cr)</label>
                      <input type="number" value={currentDetail.creditAmount} placeholder="0.00" step="0.01" min="0"
                        onChange={e => setCurrentDetail({ ...currentDetail, creditAmount: e.target.value, debitAmount: '' })}
                        className="w-full px-3 py-2.5 border border-emerald-200 bg-emerald-50 rounded-lg text-sm text-right font-mono outline-none focus:border-emerald-400" />
                    </div>
                    <div>
                      <label className={labelCls}>Narration</label>
                      <input type="text" value={currentDetail.narration} placeholder="Optional" maxLength="200"
                        onChange={e => setCurrentDetail({ ...currentDetail, narration: e.target.value })}
                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none focus:border-[#0d1c6b]" />
                    </div>
                    <div>
                      <label className="block mb-1.5 text-[11px] invisible">x</label>
                      <button type="button" onClick={addDetailLine}
                        className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer bg-emerald-500 hover:bg-emerald-600 transition-colors duration-150 whitespace-nowrap">
                        <Plus size={15} /> Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Detail table */}
                {formData.details.length > 0 ? (
                  <>
                    <div className="overflow-x-auto rounded-lg border border-gray-200 mb-3">
                      <table className="w-full border-collapse text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            {['#', 'Account Name', 'Debit (Dr)', 'Credit (Cr)', 'Narration', ''].map(h => (
                              <th key={h} className="px-3 py-2.5 text-left text-[11px] font-bold uppercase tracking-wide text-gray-500 border-b border-gray-200">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {formData.details.map((detail, index) => (
                            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150">
                              <td className="px-3 py-2.5 text-center font-bold text-blue-500 w-8">{index + 1}</td>
                              <td className="px-3 py-2.5">
                                <div className="font-semibold text-gray-800">{detail.acheadname}</div>
                                {detail.acheadcode && <div className="text-[11px] text-gray-400">{detail.acheadcode}</div>}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono font-bold text-red-600">
                                {detail.debitAmount > 0 ? `₹ ${fmtINR(detail.debitAmount)}` : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2.5 text-right font-mono font-bold text-emerald-600">
                                {detail.creditAmount > 0 ? `₹ ${fmtINR(detail.creditAmount)}` : <span className="text-gray-300">-</span>}
                              </td>
                              <td className="px-3 py-2.5 text-xs text-gray-400 italic">{detail.narration || '-'}</td>
                              <td className="px-3 py-2.5 text-center">
                                <button type="button" onClick={() => removeDetailLine(index)}
                                  className="p-1 rounded border-0 cursor-pointer bg-transparent text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors duration-150">
                                  <Trash2 size={13} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Balance summary */}
                    <div className={`grid grid-cols-3 gap-3 p-4 rounded-xl border-2 ${isBalanced() ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                      <div className="text-center">
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Total Debit (Dr)</div>
                        <div className="text-lg font-bold font-mono text-red-600">₹ {fmtINR(calculateDebitTotal())}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Total Credit (Cr)</div>
                        <div className="text-lg font-bold font-mono text-emerald-600">₹ {fmtINR(calculateCreditTotal())}</div>
                      </div>
                      <div className="text-center">
                        <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-1">Difference</div>
                        <div className={`text-lg font-bold font-mono ${isBalanced() ? 'text-emerald-700' : 'text-red-600'}`}>
                          {isBalanced()
                            ? '✅ Balanced'
                            : <>⚠️ ₹ {fmtINR(Math.abs(getDifference()))} <span className="text-xs">{getDifference() > 0 ? '(Dr excess)' : '(Cr excess)'}</span></>}
                        </div>
                      </div>
                    </div>
                    {errors.balance && (
                      <div className="flex items-center gap-2 mt-2 text-xs text-red-600 font-semibold">
                        <AlertCircle size={13} /> {errors.balance}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-2 py-10 text-gray-400 text-sm text-center">
                    <FileText size={30} className="text-gray-300" />
                    <p>No transaction lines added yet</p>
                    <small>Add at least 2 lines (one debit and one credit)</small>
                  </div>
                )}
              </div>

              {/* Narration */}
              <div>
                <label className={labelCls}>📝 Overall Narration/Description</label>
                <textarea value={formData.narration} rows={3} placeholder="Enter overall narration or description for this journal entry..." maxLength={500}
                  onChange={e => setFormData({ ...formData, narration: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm bg-white outline-none transition-all duration-150 focus:border-[#0d1c6b] focus:shadow-[0_0_0_3px_rgba(13,28,107,0.1)] resize-y font-[inherit]" />
                <p className="text-[11px] text-gray-400 mt-1 text-right">{formData.narration.length}/500 characters</p>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-5 border-t border-gray-200">
                <div>
                  {formData.details.length > 0 && (
                    <span className="text-sm text-gray-500 font-medium">
                      {formData.details.length} {formData.details.length === 1 ? 'line' : 'lines'} added
                    </span>
                  )}
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={closeModal} disabled={submitLoading}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:bg-gray-50 hover:border-gray-300 disabled:opacity-60 disabled:cursor-not-allowed">
                    <Ban size={14} /> Cancel
                  </button>
                  <button type="button" onClick={handlePrint}
                    disabled={formData.details.length === 0 || !isBalanced()}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                    <Printer size={14} /> Print
                  </button>
                  <button type="submit" disabled={submitLoading || formData.details.length < 2 || !isBalanced()}
                    className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-0 rounded-lg cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_rgba(13,28,107,0.35)] disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)`, color: '#fff' }}>
                    <Save size={14} /> {submitLoading ? 'Saving...' : (editMode ? 'Update Entry' : 'Save Entry')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── HELP MODAL ── */}
      {showHelp && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-5"
          onClick={() => setShowHelp(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[620px] max-h-[85vh] flex flex-col shadow-2xl border border-gray-200 overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-7 py-5"
              style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
              <h3 className="text-[18px] font-bold uppercase tracking-wide m-0 flex items-center gap-2" style={{ color: '#fff' }}>
                <HelpCircle size={20} /> How to Use Journal Voucher Entry
              </h3>
              <button onClick={() => setShowHelp(false)}
                className="w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer border-0 bg-white/20 text-white hover:bg-white/30 hover:rotate-90 transition-all duration-200">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-7 space-y-4">
              {[
                { title: 'Voucher Details', desc: 'The Voucher No is auto-generated. Set the transaction Date — the type is fixed as Journal Voucher (JV).' },
                { title: 'Add Transaction Lines', desc: 'Select an Account Ledger, then enter either a Debit or a Credit amount for that line (not both). Add an optional narration, then click "Add".' },
                { title: 'Double-Entry Balancing', desc: 'A journal entry needs at least 2 lines, and Total Debit must equal Total Credit. The balance summary shows "Balanced" in green once it matches, or the difference in red if not.' },
                { title: 'Duplicate an Entry', desc: 'Use the copy icon on any existing entry to pre-fill a new entry with the same lines and narration, ready to adjust and save.' },
                { title: 'Print or Save', desc: 'Once balanced, you can Print a formatted journal voucher or Save the entry. Saving is disabled until the entry balances.' },
              ].map(({ title, desc }, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 flex-shrink-0 rounded-full flex items-center justify-center text-xs font-bold text-white"
                    style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-bold text-gray-800 mb-0.5">{title}</div>
                    <div className="text-sm text-gray-600">{desc}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end px-7 py-4 border-t border-gray-200 flex-shrink-0">
              <button onClick={() => setShowHelp(false)}
                className="px-5 py-2.5 text-sm font-bold text-white border-0 rounded-lg cursor-pointer"
                style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn  { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp { from { transform:translateY(40px) scale(0.96);opacity:0 } to { transform:translateY(0) scale(1);opacity:1 } }
      `}</style>
    </div>
  );
};

export default JournalEntry;